import { useParams, Link } from 'react-router-dom';
import { Helmet } from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb, { getBreadcrumbJsonLD } from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const currentCategory = categories.find((c) => c.id === categoryId);
  const categoryName = currentCategory?.name || '全部';

  const filteredComics =
    categoryId === 'all' ? comics : comics.filter((c) => c.categoryId === categoryId);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '漫画分类', href: '/category/all' },
    { label: categoryName },
  ];

  const breadcrumbSchema = getBreadcrumbJsonLD(
    breadcrumbItems,
    'https://www.hanmanzhijia.com'
  );

  const categorySchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: `${categoryName}韩漫 - 韩漫之家`,
        description: `韩漫之家${categoryName}分类，提供最新高清免费${categoryName}韩国漫画在线阅读，每日更新。`,
        url: `https://www.hanmanzhijia.com/category/${categoryId}`,
      },
      breadcrumbSchema,
    ],
  };

  return (
    <>
      <Helmet
        title={`${categoryName}韩漫_免费${categoryName}韩国漫画在线阅读-韩漫之家`}
        description={`韩漫之家${categoryName}分类页面，提供最新高清免费${categoryName}韩国漫画大全，热门${categoryName}作品均可在线阅读。每日更新，畅快阅读。`}
        keywords={`${categoryName}韩漫,${categoryName}韩国漫画,韩漫${categoryName},免费${categoryName}漫画`}
        canonicalUrl={`https://www.hanmanzhijia.com/category/${categoryId}`}
        ogTitle={`${categoryName}韩漫 - 免费韩国漫画在线阅读 - 韩漫之家`}
        ogDescription={`韩漫之家${categoryName}分类，提供最新高清免费${categoryName}韩国漫画在线阅读。`}
        ogImage="https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20category%20page%20banner%2C%20artistic%20watercolor%20style%2C%20warm%20coral%20and%20cream%20tones%2C%20comic%20collection%20display&width=1200&height=630&seq=og-category&orientation=landscape"
        schema={categorySchema}
      />
      <main className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 pb-8 md:pb-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="mt-6 mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-1 h-6 rounded-full bg-primary-500"></div>
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  {categoryName}漫画
                </h1>
              </div>
              <p className="text-sm text-foreground-600">
                共找到 <strong>{filteredComics.length}</strong> 部{categoryName === '全部' ? '' : categoryName}漫画
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8 bg-background-100 p-1 rounded-full">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    categoryId === cat.id
                      ? 'bg-primary-500 text-background-50 shadow-sm'
                      : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-200'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} variant="default" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </div>
                <p className="text-foreground-600">该分类暂无漫画，敬请期待！</p>
              </div>
            )}
          </div>
        </div>

        <Footer />
        <ScrollToTop />
      </main>
    </>
  );
}